<template>
  <div class="d-flex" style="gap: 12px;">
    <v-card
      v-for="item in filteredPresets" :key="item.value"
      class="d-flex justify-center align-center pa-3 pa-md-6 relative"
      v-bind:class="{ active: selected === item }"
      v-bind:style="{ 'background-color': item.skin.page_background_color || DEFAULT.page_background_color }"
      hover
      :disabled="disabled"
      @click="$emit('input', { ...order.skin, ...DEFAULT_THEME, ...item.skin, ...(orgSkin || {}) })"
    >
      <v-lazy v-if="item.skin.open_effect">
        <confetti
          class="absolute"
          style="z-index: 2; left: -50%; top: -50%; width: 200%; height: 200%; transform: scale(.5);"
          :effect="item.skin.open_effect"
          :active="true"
        />
      </v-lazy>
      <v-card
        v-bind:style="{
          'background-color': item.skin.env_background_color || DEFAULT.env_background_color,
          'color': item.skin.env_font_color || DEFAULT.env_font_color,
          'font-family': item.skin.env_font || DEFAULT.env_font,
          'font-size': fontSize
        }"
        tile
        :width="width"
      >
        <v-responsive :aspect-ratio="7 / 5">
          <div class="fill-height d-flex justify-center align-center text-center">
            {{item.value}}
          </div>
        </v-responsive>
      </v-card>
    </v-card>
    <div v-if="nHiddenPresets" class="d-flex align-center text--label-active font-italic" style="min-width: 120px;">
      Your organization's custom branding is hiding {{nHiddenPresets}} preset{{nHiddenPresets == 1 ? '' : 's'}}
    </div>
  </div>
</template>


<style>
.v-card--link.active {
  outline: 2px solid rgb(25, 118, 210);
}
.v-card--link.active:before {
  opacity: 0.08;
}
</style>


<style scoped>
.round-left {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}
.round-right {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}
</style>


<script>
import Confetti from '@/components/Confetti.vue';
import OutlineLaptop from '@/components/OutlineLaptop.vue';
import OutlinePhone from '@/components/OutlinePhone.vue';
import SkinPreview from '@/components/SkinPreview.vue';
import { nullCoverDesignId } from '@/mixins/public-design.js';
import loadFont from '@/utils/load-font.js';
import { DEFAULT, EFFECTS } from '@/utils/skin.js';
import { mdiPlus } from '@mdi/js';


// A map of serialized JSON effects to their short-form key (inverse of the EFFECTS map)
const LONG_FORM_EFFECTS = Object.fromEntries(
  Object.entries(EFFECTS).map(([ key, value ]) => [ JSON.stringify(value), key ])
);

function vColorPickerValue(v) {
  return v ? (/^#...$/.test(v) ? '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3] : v).toUpperCase() : '';
}

const COMPARABLES = {
  env_font: x => x,
  env_font_color: vColorPickerValue,
  env_background_color: vColorPickerValue,
  page_background_color: vColorPickerValue,
  open_effect: x => LONG_FORM_EFFECTS[x] || x
};

const PRESET_KEYS = Object.keys(COMPARABLES);

const DEFAULT_THEME = _.pick(DEFAULT, PRESET_KEYS);

const PRESETS = [
  {
    value: 'Plain',
    skin: DEFAULT_THEME,
  },
  {
    value: 'Happy',
    skin: {
      env_font: 'Chewy',
      env_font_color: '#1976D2',
      env_background_color: '#B3E5FC',
      page_background_color: '#FFF176',
      open_effect: 'confetti'
    }
  },
  {
    value: 'Playful',
    skin: {
      env_font: 'Indie Flower',
      env_font_color: '#FFEE58',
      env_background_color: '#4CAF50',
      page_background_color: '#FFCCBC',
      open_effect: 'confetti'
    }
  },
  {
    value: 'Sincere',
    skin: {
      env_font: 'Sacramento',
      env_font_color: '#EEEEEE',
      env_background_color: '#B71C1C',
      page_background_color: '#CFD8DC',
      open_effect: null
    }
  },
  {
    value: 'Romantic',
    skin: {
      env_font: 'Kaushan Script',
      env_font_color: '#FCE4EC',
      env_background_color: '#F06292',
      page_background_color: '#FFECB3',
      open_effect: 'romantic'
    }
  },
  {
    value: 'Party',
    skin: {
      env_font: 'Fredericka the Great',
      env_font_color: '#9C27B0',
      env_background_color: '#FFD54F',
      page_background_color: '#F8BBD0',
      open_effect: 'confetti'
    }
  },
  {
    value: 'Personal',
    skin: {
      env_font: 'Homemade Apple',
      env_font_color: '#E3F2FD',
      env_background_color: '#2196F3',
      page_background_color: '#BBDEFB',
      open_effect: null
    }
  },
  {
    value: 'Professional',
    skin: {
      env_font: 'Amatic SC',
      env_font_color: '#EFEBE9',
      env_background_color: '#795548',
      page_background_color: '#D7CCC8',
      open_effect: null
    }
  },
  {
    value: 'Winter',
    skin: {
      env_font: 'Lobster',
      env_font_color: '#607D8B',
      env_background_color: '#ECEFF1',
      page_background_color: '#B0BEC5',
      open_effect: 'snow'
    }
  },
  {
    value: 'Spring',
    skin: {
      env_font: 'Emilys Candy',
      env_font_color: '#1B5E20',
      env_background_color: '#9CCC65',
      page_background_color: '#C5E1A5',
      open_effect: 'rain'
    }
  },
  {
    value: 'Summer',
    skin: {
      env_font: 'Homemade Apple',
      env_font_color: '#0D47A1',
      env_background_color: '#FFEB3B',
      page_background_color: '#BBDEFB',
      open_effect: 'summer'
    }
  },
  {
    value: 'Fall',
    skin: {
      env_font: 'Ranchers',
      env_font_color: '#5D4037',
      env_background_color: '#FF9800',
      page_background_color: '#FFCC80',
      open_effect: 'fall'
    }
  }
];


function isSelected(skin, orgSkin, preset) {
  for (const k of PRESET_KEYS)
    if (COMPARABLES[k](skin[k]) != COMPARABLES[k](orgSkin.hasOwnProperty(k) ? orgSkin[k] : preset.skin[k]))
      return false;
  return true;
}


export default {
  name: 'select-theme-ribbon',

  components: {
    Confetti,
    OutlineLaptop,
    OutlinePhone,
    SkinPreview
  },

  props: [ 'order', 'orgSkin', 'label', 'hint', 'disabled', 'width', 'fontSize' ],

  data() {
    return {
      userThemes: []
    };
  },

  computed: {
    filteredPresets() {
      if (!this.orgSkin)
        return PRESETS;
      // Substitute any properties from the presets that would be overridden by the org. This could result in duplicate
      // presets that should be removed (giving precedence to earlier ones).
      const orgTheme = this.orgSkin ? _.pick(this.orgSkin, PRESET_KEYS) : null;
      const presets = PRESETS.map(x => ({ ...x, skin: Object.assign({}, x.skin, orgTheme) }));
      return presets.filter((x, i) => !presets.find((y, j) => (j < i) && _.isEqual(x.skin, y.skin)));
    },

    nHiddenPresets() {
      return PRESETS.length - this.filteredPresets.length;
    },

    selected() {
      return this.filteredPresets.find(x => isSelected(this.order.skin, this.orgSkin, x));
    }
  },

  created() {
    Object.assign(this, { DEFAULT, DEFAULT_THEME, mdiPlus });
  },

  watch: {
    filteredPresets: {
      immediate: true,
      handler(v) {
        v.map(x => loadFont(x.skin.env_font));
      }
    },

    selected: {
      immediate: true,
      handler(v) {
        this.$emit('is-match', !!v);
      }
    }
  }
}
</script>
